/**
 * Built-in sample kit — fully procedural, no audio files.
 * Each generator returns mono samples at the context's sample rate.
 */

export interface BuiltinSample {
  id: string;
  name: string;
  rootNote: number;
  make: (sr: number) => Float32Array<ArrayBuffer>;
}

function normalize(d: Float32Array<ArrayBuffer>, peak = 0.85): Float32Array<ArrayBuffer> {
  let max = 0;
  for (let i = 0; i < d.length; i++) max = Math.max(max, Math.abs(d[i]));
  if (max > 0) {
    const k = peak / max;
    for (let i = 0; i < d.length; i++) d[i] *= k;
  }
  return d;
}

/** Karplus-Strong plucked string. */
function karplusStrong(
  sr: number,
  freq: number,
  seconds: number,
  decay = 0.996,
): Float32Array<ArrayBuffer> {
  const N = Math.max(2, Math.round(sr / freq));
  const len = Math.floor(sr * seconds);
  const ring = new Float32Array(N);
  for (let i = 0; i < N; i++) ring[i] = Math.random() * 2 - 1;
  const out = new Float32Array(len);
  for (let i = 0; i < len; i++) {
    const cur = ring[i % N];
    ring[i % N] = decay * 0.5 * (cur + ring[(i + 1) % N]);
    out[i] = cur;
  }
  return normalize(out);
}

/** Additive tone with exponentially decaying partials. */
function additive(
  sr: number,
  freq: number,
  seconds: number,
  partials: Array<[mult: number, gain: number, decay: number]>,
): Float32Array<ArrayBuffer> {
  const len = Math.floor(sr * seconds);
  const out = new Float32Array(len);
  for (const [mult, gain, decay] of partials) {
    const f = freq * mult;
    for (let i = 0; i < len; i++) {
      const t = i / sr;
      out[i] += Math.sin(2 * Math.PI * f * t) * gain * Math.exp(-t * decay);
    }
  }
  return normalize(out);
}

/** Decaying sine with a pitch sweep (kick drum). */
function kick(sr: number, seconds = 0.45): Float32Array<ArrayBuffer> {
  const len = Math.floor(sr * seconds);
  const out = new Float32Array(len);
  let phase = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    const f = 42 + 130 * Math.exp(-t * 28);
    phase += (2 * Math.PI * f) / sr;
    out[i] = Math.sin(phase) * Math.exp(-t * 7.5);
  }
  // Short noise click for the transient.
  const click = Math.floor(sr * 0.004);
  for (let i = 0; i < click; i++) {
    out[i] += (Math.random() * 2 - 1) * Math.exp(-i / (sr * 0.0008)) * 0.4;
  }
  return normalize(out);
}

/** One-pole high-passed noise burst with exponential decay. */
function noiseBurst(
  sr: number,
  seconds: number,
  hp: number,
  decay: number,
): Float32Array<ArrayBuffer> {
  const len = Math.floor(sr * seconds);
  const out = new Float32Array(len);
  const rc = 1 / (1 + (2 * Math.PI * hp) / sr);
  let yPrev = 0;
  let xPrev = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    const x = Math.random() * 2 - 1;
    const y = rc * (yPrev + x - xPrev);
    xPrev = x;
    yPrev = y;
    out[i] = y * Math.exp(-t * decay);
  }
  return out;
}

/** Snare: high-passed noise bed + 190 Hz body tone. */
function snare(sr: number, seconds = 0.3): Float32Array<ArrayBuffer> {
  const len = Math.floor(sr * seconds);
  const out = new Float32Array(len);
  const rc = 1 / (1 + (2 * Math.PI * 1600) / sr);
  let yPrev = 0;
  let xPrev = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    const x = Math.random() * 2 - 1;
    const y = rc * (yPrev + x - xPrev);
    xPrev = x;
    yPrev = y;
    const body = Math.sin(2 * Math.PI * 190 * t) * Math.exp(-t * 25);
    out[i] = y * Math.exp(-t * 18) * 0.9 + body * 0.7;
  }
  return normalize(out);
}

/** Clap: three fast noise bursts followed by a longer tail. */
function clap(sr: number, seconds = 0.35): Float32Array<ArrayBuffer> {
  const len = Math.floor(sr * seconds);
  const out = new Float32Array(len);
  const rc = 1 / (1 + (2 * Math.PI * 1200) / sr);
  const bursts = [0, 0.012, 0.024];
  let yPrev = 0;
  let xPrev = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    const x = Math.random() * 2 - 1;
    const y = rc * (yPrev + x - xPrev);
    xPrev = x;
    yPrev = y;
    let env = 0;
    for (const b of bursts) {
      const dt = t - b;
      if (dt >= 0) env = Math.max(env, Math.exp(-dt * 90));
    }
    const dtTail = t - 0.036;
    if (dtTail >= 0) env = Math.max(env, Math.exp(-dtTail * 14));
    out[i] = y * env;
  }
  return normalize(out);
}

/** Hi-hat: very high-passed noise. */
function hat(sr: number, seconds: number, hp: number, decay: number): Float32Array<ArrayBuffer> {
  return normalize(noiseBurst(sr, seconds, hp, decay), 0.7);
}

/** Rim shot: short detuned square blip. */
function rim(sr: number, seconds = 0.06): Float32Array<ArrayBuffer> {
  const len = Math.floor(sr * seconds);
  const out = new Float32Array(len);
  let phase = 0;
  for (let i = 0; i < len; i++) {
    const t = i / sr;
    phase += (2 * Math.PI * 950) / sr;
    out[i] = Math.sign(Math.sin(phase)) * Math.exp(-t * 80);
  }
  return normalize(out, 0.7);
}

export const BUILTIN_SAMPLES: BuiltinSample[] = [
  {
    id: 'builtin-pluck',
    name: 'Pluck (C4)',
    rootNote: 60,
    make: (sr) => karplusStrong(sr, 261.63, 2.5, 0.9965),
  },
  {
    id: 'builtin-piano',
    name: 'Piano (C4)',
    rootNote: 60,
    make: (sr) =>
      additive(sr, 261.63, 2.2, [
        [1, 1.0, 3.2],
        [2, 0.5, 4.5],
        [3, 0.28, 6],
        [4, 0.16, 7.5],
      ]),
  },
  {
    id: 'builtin-bass',
    name: 'Bass (C2)',
    rootNote: 36,
    make: (sr) =>
      additive(sr, 65.41, 1.6, [
        [1, 1.0, 5],
        [2, 0.35, 6],
        [0.5, 0.15, 4],
      ]),
  },
  {
    id: 'builtin-bell',
    name: 'Bell (C5)',
    rootNote: 72,
    make: (sr) =>
      additive(sr, 523.25, 3.0, [
        [1, 1.0, 2.2],
        [2.76, 0.4, 3.0],
        [5.4, 0.2, 4.2],
      ]),
  },
  // Procedural percussion — usable on a sampler channel (or via the browser).
  {
    id: 'builtin-kick',
    name: 'Kick',
    rootNote: 36,
    make: (sr) => kick(sr),
  },
  {
    id: 'builtin-snare',
    name: 'Snare',
    rootNote: 60,
    make: (sr) => snare(sr),
  },
  {
    id: 'builtin-clap',
    name: 'Clap',
    rootNote: 60,
    make: (sr) => clap(sr),
  },
  {
    id: 'builtin-hat-closed',
    name: 'Hat Closed',
    rootNote: 72,
    make: (sr) => hat(sr, 0.08, 7000, 40),
  },
  {
    id: 'builtin-hat-open',
    name: 'Hat Open',
    rootNote: 72,
    make: (sr) => hat(sr, 0.4, 6000, 8),
  },
  {
    id: 'builtin-rim',
    name: 'Rim',
    rootNote: 60,
    make: (sr) => rim(sr),
  },
];